import { useState, useEffect, useCallback } from "react";

import {
  getBusinessProfile,
  updateBusinessProfile,

  getBusinessAddress,
  updateBusinessAddress,

  getBankDetails,
  updateBankDetails,

  getWallet,

  updateBusinessHours,

  updateBusinessStatus,

  updateBusinessPhotos,

  updateBusinessDocuments,
} from "../services/businessService";

const useBusiness = () => {
  const [business, setBusiness] = useState(null);
  const [address, setAddress] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  // ==========================================
  // Load All Data
  // ==========================================

  const loadBusiness = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [
        businessRes,
        addressRes,
        bankRes,
        walletRes,
      ] = await Promise.all([
        getBusinessProfile(),
        getBusinessAddress(),
        getBankDetails(),
        getWallet(),
      ]);

      setBusiness(businessRes.business);

      setAddress(addressRes.address);

      setBankDetails(bankRes.bankDetails);

      setWallet(walletRes.wallet);

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to load business details."
      );

    } finally {

      setLoading(false);

    }
  }, []);

  useEffect(() => {
    loadBusiness();
  }, [loadBusiness]);

  // ==========================================
  // Update Business Profile
  // ==========================================

  const saveBusinessProfile = async (data) => {
    try {
      setSaving(true);

      await updateBusinessProfile(data);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update profile."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Hours
  // ==========================================

  const saveBusinessHours = async (data) => {
    try {

      setSaving(true);

      await updateBusinessHours(data);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update hours."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Address
  // ==========================================

  const saveAddress = async (data) => {
    try {

      setSaving(true);

      await updateBusinessAddress(data);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update address."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Bank
  // ==========================================

  const saveBank = async (data) => {
    try {

      setSaving(true);

      await updateBankDetails(data);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update bank details."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Status
  // ==========================================

  const saveStatus = async (data) => {
    try {

      setSaving(true);

      await updateBusinessStatus(data);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update status."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Photos
  // ==========================================

  const savePhotos = async (formData) => {
    try {

      setSaving(true);

      await updateBusinessPhotos(formData);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update photos."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Update Documents
  // ==========================================

  const saveDocuments = async (formData) => {
    try {

      setSaving(true);

      await updateBusinessDocuments(formData);

      await loadBusiness();

      return true;

    } catch (err) {

      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update documents."
      );

      return false;

    } finally {

      setSaving(false);

    }
  };

  // ==========================================
  // Return
  // ==========================================

  return {

    business,

    address,

    bankDetails,

    wallet,

    loading,

    saving,

    error,

    refresh: loadBusiness,

    saveBusinessProfile,

    saveBusinessHours,

    saveAddress,

    saveBank,

    saveStatus,

    savePhotos,

    saveDocuments,

  };
};

export default useBusiness;